import { spiffsSafeCatalogPath } from '../files/catalogPaths';
import {
  FlowLayout,
  GraphRoute,
  SettingsBag,
  ensureGraph,
  normalizeRoute,
  readFlowLayout,
  readRoutes,
} from './graph';

export type FlowCloudWidget = {
  id: string;
  type?: string;
  title?: string;
  topic?: string;
  topicRh?: string;
  [key: string]: unknown;
};

export type FlowFileDocument = {
  id?: string;
  enable?: boolean;
  layouts?: string[];
  layout?: string;
  routes: GraphRoute[];
  flow: FlowLayout;
  cloud?: { widgets?: FlowCloudWidget[]; routes?: GraphRoute[] };
};

export function defaultFlowFile(id = 'default'): FlowFileDocument {
  return {
    id,
    enable: true,
    layouts: [],
    routes: [],
    flow: { nodes: {} },
  };
}

/** Catalog example: climate values split to the display and to MQTT → cloud → dashboard. */
export function climateExampleFlow(): FlowFileDocument {
  return {
    id: 'climate',
    enable: true,
    layouts: ['climate'],
    routes: [
      {
        id: 'temp_label',
        enable: true,
        when: 'interval',
        interval: 60,
        from: 'climate.temp',
        to: 'lvgl.climate.temp',
        transform: { scale: 0.1, unit: 'C' },
      },
      {
        id: 'rh_label',
        enable: true,
        when: 'interval',
        interval: 60,
        from: 'climate.rh',
        to: 'lvgl.climate.rh',
        transform: { scale: 0.1, unit: '%' },
      },
      {
        id: 'temp_mqtt',
        enable: true,
        when: 'interval',
        interval: 60,
        from: 'climate.temp',
        to: 'mqtt.{base}/climate/temp',
        transform: { scale: 0.1, unit: 'C' },
      },
      {
        id: 'rh_mqtt',
        enable: true,
        when: 'interval',
        interval: 60,
        from: 'climate.rh',
        to: 'mqtt.{base}/climate/rh',
        transform: { scale: 0.1, unit: '%' },
      },
    ],
    flow: {
      nodes: {
        't:60': { x: 40, y: 380, kind: 'trigger', label: '60s', interval: 60 },
        'g:klima': { x: 40, y: 100, kind: 'controller', label: 'klima', role: 'klima' },
        'd:rs485': { x: 220, y: 100, kind: 'device', deviceType: 'modbus', label: 'Modbus', ownerId: 'g:klima' },
        'd:climate': { x: 400, y: 100, kind: 'device', deviceType: 'modbus_slave', label: 'climate' },
        'p:climate:temp': { x: 600, y: 20, kind: 'point', label: 'temp', busId: 'climate', pointKey: 'temp' },
        'p:climate:rh': { x: 600, y: 220, kind: 'point', label: 'rh', busId: 'climate', pointKey: 'rh' },
        'x:p:climate:temp:0.1': { x: 800, y: 20, kind: 'transform', label: '×0.1 C', transform: { scale: 0.1, unit: 'C' } },
        'x:p:climate:rh:0.1': { x: 800, y: 220, kind: 'transform', label: '×0.1 %', transform: { scale: 0.1, unit: '%' } },
        'k:temp_label': { x: 800, y: 130, kind: 'sink', label: 'Climate · temp', to: 'lvgl.climate.temp', layoutId: 'climate' },
        'k:rh_label': { x: 800, y: 330, kind: 'sink', label: 'Climate · rh', to: 'lvgl.climate.rh', layoutId: 'climate' },
        'm:base_climate_temp': {
          x: 1040,
          y: 20,
          kind: 'device',
          deviceType: 'mqtt',
          label: '{base}/climate/temp',
          topic: '{base}/climate/temp',
        },
        'm:base_climate_rh': {
          x: 1040,
          y: 220,
          kind: 'device',
          deviceType: 'mqtt',
          label: '{base}/climate/rh',
          topic: '{base}/climate/rh',
        },
        'c:m:base_climate_temp': { x: 1280, y: 20, kind: 'cloudTransform', label: 'Cloud' },
        'c:m:base_climate_rh': { x: 1280, y: 220, kind: 'cloudTransform', label: 'Cloud' },
        'k:dash:climate_dash': {
          x: 1500,
          y: 100,
          kind: 'sink',
          label: 'Climate',
          to: 'dashboard.climate_dash',
          widgetType: 'temperature',
          topic: '{base}/climate/temp',
          topicRh: '{base}/climate/rh',
        },
      },
    },
    cloud: {
      widgets: [{
        id: 'climate_dash',
        type: 'temperature',
        title: 'Climate',
        topic: '{base}/climate/temp',
        topicRh: '{base}/climate/rh',
      }],
    },
  };
}

/** Catalog example: Gerät → 4× WS2812 sticks → Säule → MQTT → Cloud → Widget. */
export function stacklightExampleFlow(): FlowFileDocument {
  return {
    id: 'stacklight',
    enable: true,
    layouts: [],
    routes: [],
    flow: {
      nodes: {
        'g:halle': { x: 40, y: 130, kind: 'controller', label: 'halle', role: 'halle' },
        'd:strip_1': {
          x: 220, y: 40, kind: 'device', deviceType: 'ws2812', label: 'Strip 1',
        },
        'd:strip_2': {
          x: 220, y: 110, kind: 'device', deviceType: 'ws2812', label: 'Strip 2',
        },
        'd:strip_3': {
          x: 220, y: 180, kind: 'device', deviceType: 'ws2812', label: 'Strip 3',
        },
        'd:strip_4': {
          x: 220, y: 250, kind: 'device', deviceType: 'ws2812', label: 'Strip 4',
        },
        's:default': {
          x: 440,
          y: 130,
          kind: 'device',
          deviceType: 'stacklight',
          label: 'default',
          signalName: 'default',
          topic: 'stacklight1',
          ownerId: 'g:halle',
          strips: ['strip_1', 'strip_2', 'strip_3', 'strip_4'],
        },
        'm:stacklight1': {
          x: 660,
          y: 130,
          kind: 'device',
          deviceType: 'mqtt',
          label: 'stacklight1',
          topic: 'stacklight1',
        },
        'c:m:stacklight1': { x: 880, y: 130, kind: 'cloudTransform', label: 'Cloud' },
        'k:dash:stacklight_default': {
          x: 1100,
          y: 130,
          kind: 'sink',
          label: 'Säule',
          to: 'dashboard.stacklight_default',
          widgetType: 'stacklight',
          topic: 'stacklight1',
          signalName: 'default',
        },
      },
    },
    cloud: {
      widgets: [{
        id: 'stacklight_default',
        type: 'stacklight',
        title: 'Säule',
        topic: 'stacklight1',
        signalName: 'default',
      }],
    },
  };
}

function readLayoutIds(parsed: Record<string, unknown>, routes: GraphRoute[]): string[] {
  const listed = Array.isArray(parsed.layouts)
    ? parsed.layouts.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const single = typeof parsed.layout === 'string' && parsed.layout.trim() ? [parsed.layout.trim()] : [];
  const fromRoutes = layoutIdsFromRoutes(routes);
  return Array.from(new Set(listed.concat(single, fromRoutes))).sort();
}

export function layoutIdsFromRoutes(routes: GraphRoute[]): string[] {
  const ids = new Set<string>();
  for (const route of routes) {
    const dest = String(route.to || '');
    if (!dest.startsWith('lvgl.')) {
      continue;
    }
    const rest = dest.slice('lvgl.'.length);
    const dot = rest.indexOf('.');
    if (dot > 0) {
      ids.add(rest.slice(0, dot));
    }
  }
  return Array.from(ids).sort();
}

function isFlowLayout(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const nodes = (value as { nodes?: unknown }).nodes;
  return Boolean(nodes) && typeof nodes === 'object' && !Array.isArray(nodes);
}

/** `{ flow: document }` from axios, or a nested save that stuffed the document into `flow`. */
function isNestedFlowDocument(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value) || isFlowLayout(value)) {
    return false;
  }
  const inner = value as Record<string, unknown>;
  return Array.isArray(inner.routes)
    || Array.isArray(inner.schedules)
    || isFlowLayout(inner.flow)
    || isNestedFlowDocument(inner.flow);
}

export function unwrapFlowDocument(parsed: unknown): Record<string, unknown> {
  let cur = parsed;
  for (let i = 0; i < 6; i += 1) {
    if (!cur || typeof cur !== 'object' || Array.isArray(cur)) {
      break;
    }
    const obj = cur as Record<string, unknown>;
    if (!isNestedFlowDocument(obj.flow)) {
      break;
    }
    cur = obj.flow;
  }
  return cur && typeof cur === 'object' && !Array.isArray(cur)
    ? cur as Record<string, unknown>
    : {};
}

function asCloud(raw: unknown): FlowFileDocument['cloud'] | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }
  const widgets = Array.isArray((raw as { widgets?: unknown }).widgets)
    ? (raw as { widgets: Record<string, unknown>[] }).widgets
      .filter((item) => item && typeof item === 'object')
      .map((item) => ({ ...item, id: String(item.id || '') }))
      .filter((item) => item.id)
    : [];
  const routes = Array.isArray((raw as { routes?: unknown }).routes)
    ? (raw as { routes: GraphRoute[] }).routes
      .filter((item) => item && typeof item === 'object' && !Array.isArray(item))
      .map((item) => normalizeRoute(item))
      .filter((item) => String(item.from || '').trim())
    : [];
  if (!widgets.length && !routes.length) {
    return undefined;
  }
  return {
    ...(widgets.length ? { widgets } : {}),
    ...(routes.length ? { routes } : {}),
  };
}

export function parseFlowFile(raw: string, id?: string): FlowFileDocument {
  try {
    const parsed = unwrapFlowDocument(JSON.parse(String(raw || '{}')));
    if (parsed && typeof parsed === 'object' && (Array.isArray(parsed.routes) || parsed.flow || Array.isArray(parsed.schedules))) {
      const routes = Array.isArray(parsed.routes)
        ? parsed.routes.filter((item: unknown) => item && typeof item === 'object').map((item: GraphRoute) => normalizeRoute(item))
        : [];
      return {
        id: typeof parsed.id === 'string' ? parsed.id : id,
        enable: parsed.enable !== false,
        layouts: readLayoutIds(parsed as Record<string, unknown>, routes),
        routes,
        flow: isFlowLayout(parsed.flow)
          ? parsed.flow as FlowFileDocument['flow']
          : { nodes: {} },
        cloud: asCloud(parsed.cloud),
      };
    }
    const graph = ensureGraph(parsed as SettingsBag);
    const routes = readRoutes(graph);
    return {
      id,
      enable: true,
      layouts: layoutIdsFromRoutes(routes),
      routes,
      flow: readFlowLayout(graph),
    };
  } catch {
    return defaultFlowFile(id);
  }
}

export function serializeFlowFile(doc: FlowFileDocument): string {
  const routes = (doc.routes || []).map((route) => normalizeRoute(route));
  const layouts = Array.from(new Set((doc.layouts || []).concat(layoutIdsFromRoutes(routes)))).sort();
  const body: Record<string, unknown> = {
    id: doc.id,
    enable: doc.enable !== false,
    layouts,
    routes,
    flow: doc.flow,
  };
  if (doc.cloud?.widgets?.length || doc.cloud?.routes?.length) {
    const cloud: NonNullable<FlowFileDocument['cloud']> = {};
    if (doc.cloud.widgets?.length) {
      cloud.widgets = doc.cloud.widgets;
    }
    if (doc.cloud.routes?.length) {
      cloud.routes = doc.cloud.routes.map((route) => normalizeRoute(route));
    }
    body.cloud = cloud;
  }
  return `${JSON.stringify(body, null, 2)}\n`;
}

/** On-device copy: routes only. Canvas and cloud widgets stay in the catalog. */
export function serializeDeviceFlowFile(doc: FlowFileDocument): string {
  const routes = (doc.routes || []).map((route) => normalizeRoute(route)).filter((route) => route.id && route.from);
  return `${JSON.stringify({
    id: doc.id,
    enable: doc.enable !== false,
    routes,
  }, null, 2)}\n`;
}

export function deviceFlowPath(id: string): string {
  return spiffsSafeCatalogPath('flows', id);
}

export function isDeviceFlowPath(path: string): boolean {
  return /^\/flows\/[A-Za-z0-9][A-Za-z0-9._-]{0,63}\.json$/.test(path)
    || /^\/flow[0-9]+\.json$/i.test(path);
}

export function mergeBaseWithFlowFile(base: SettingsBag, doc: FlowFileDocument): SettingsBag {
  return ensureGraph({
    ...base,
    routes: doc.routes,
    flow: doc.flow,
    cloud: doc.cloud,
  });
}

export function extractFlowFileFromSettings(settings: SettingsBag, id?: string): FlowFileDocument {
  const graph = ensureGraph(settings);
  const routes = readRoutes(graph);
  const cloud = asCloud(graph.cloud);
  return {
    id,
    enable: true,
    layouts: layoutIdsFromRoutes(routes),
    routes,
    flow: readFlowLayout(graph),
    cloud,
  };
}
